import { z } from 'zod'
import { getImpersonator, getTokenSilently } from '../auth0'
import { trackBoxChange } from '../utils/tracking/BOMTracking'

const SDUI_SERVICE_URL = process.env.REACT_APP_SDUI_SERVICE_URL

function isMobile() {
  const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
  return regex.test(navigator.userAgent)
}

async function getAuthorizationHeaders(): Promise<HeadersInit> {
  const impersonator = getImpersonator()

  if (impersonator) {
    return {
      'x-cook-impersonation-secret': impersonator.secret,
      'x-cook-impersonation-email': impersonator.email
    }
  }

  const token = await getTokenSilently()

  return {
    Authorization: token ? `${token}` : ''
  }
}

async function getHeaders() {
  return {
    platform: isMobile() ? `mobile-web` : 'web',
    'accept-version': '1.25.0',
    'Content-type': 'application/json',
    ...(await getAuthorizationHeaders())
  }
}

async function fetchSDUI(url: string, options: RequestInit = {}) {
  const headers = await getHeaders()
  return fetch(`${SDUI_SERVICE_URL}${url}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...headers,
      ...options.headers
    }
  })
}

/**
 * Fetch and parse the response from SDUI.
 * Allow an optional schema to be provided to parse the response.
 * If no schema is provided, you can still provide a type to infer the response type.
 * If both are not provided, the response is returned as any.
 */
async function fetchAndParseSDUI<
  Schema extends z.ZodTypeAny,
  Type = z.infer<Schema>
>(url: string, options: RequestInit = {}, schema?: Schema): Promise<Type> {
  const response = await fetchSDUI(url, options)

  if (!response.ok) {
    let error

    try {
      error = await response.json()
    } catch (e) {
      if (e instanceof Error) {
        throw new Error(`Failed to fetch url ${url}`)
      }
    }

    throw new Error(`Failed to fetch ${url}: ${error.error}`)
  }

  const data = await response.json()

  if (schema) {
    return schema.parse(data)
  }

  return data as Type
}

export const getForethoughtToken = async () => {
  return fetchAndParseSDUI(
    '/forethought/token',
    undefined,
    z
      .object({
        token: z.string().min(1)
      })
      .transform(data => data.token)
  )
}

export const fetchOrdersSwimlanes = async (utm: string) => {
  const searchParams = new URLSearchParams()
  if (utm) {
    searchParams.set('utm', utm)
  }

  return fetchSDUI(`/view/tabs?${searchParams.toString()}`)
}

export const fetchOrderSwimlaneByDate = async (date: string, path: string) => {
  return fetchSDUI(path ?? `/view/tabs/${date}/content`)
}

export const getViewHome = async (date: string | null | undefined = '') => {
  return date ? fetchSDUI(`/view/home/${date}`) : fetchSDUI(`/view/home`)
}

export const getViewHomeHeader = async (
  date: string | null | undefined = ''
) => {
  return date
    ? fetchSDUI(`/view/home/header/${date}`)
    : fetchSDUI(`/view/home/header`)
}
export const getViewExpress = async (date: string | null | undefined = '') => {
  return fetchSDUI(`/view/express?date=${date}`)
}

export const fetchUserPreferencesData = async () => {
  return fetchAndParseSDUI('/preferences')
}

export const fetchUserPreferencesView = async () => {
  return fetchAndParseSDUI('/view/preferences')
}

export const saveUserPreferencesData = async payload => {
  return fetchSDUI('/preferences', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export const removeUserProfileRequest = async payload => {
  return fetchSDUI('/preferences/removeProfile', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export const getCartForDate = async (date: string | null | undefined = '') => {
  return fetchSDUI(`/menu/cart/${date}`)
}

export const addProduct = async (
  date: string,
  inventoryId: string,
  quantity: number,
  entityId: string
) => {
  return fetchSDUI(`/menu/v2/cart/${date}/product/${inventoryId}`, {
    method: 'POST',
    body: JSON.stringify({ quantity, entityId })
  })
}

export const removeProduct = async (
  date: string,
  inventoryId: string,
  entityId: string
) => {
  return fetchSDUI(`/menu/v2/cart/${date}/product/${inventoryId}`, {
    method: 'DELETE',
    body: JSON.stringify({ entityId })
  })
}

export const removeAllProducts = async (date: string) => {
  return fetchSDUI(`/menu/cart/${date}`, {
    method: 'DELETE'
  })
}

export const getViewProductsList = async ({ date, filter, filterOption }) => {
  const url = `/view/home/${date}/product-list?filter=${filter}&filterOption=${filterOption}`
  return fetchSDUI(url)
}

export const getProductsList = async (params: string) => {
  // Add default limit if not present
  let modifiedParams = params
  if (!params.includes('limit=')) {
    modifiedParams = `${params}&limit=100`
  }
  return fetchSDUI(`/products?${modifiedParams}`)
}

export const getUnifiedCartForDate = async (date: string) => {
  return fetchSDUI(`/cart/v2/${date}`)
}

export const addProductsToUnifiedCart = async (
  cartId: number,
  products: any
) => {
  return fetchSDUI(`/cart/v2/${cartId}/products`, {
    method: 'POST',
    body: JSON.stringify({ products })
  })
}

export const changeBoxPlanForCart = async (cartId: number, boxId: number) => {
  trackBoxChange(boxId)

  return fetchAndParseSDUI(`/cart/v2/${cartId}/options`, {
    method: 'PATCH',
    body: JSON.stringify({ optionId: boxId })
  })
}

export const changeSelectedMealPackForCart = async (
  cartId: number,
  mealPackId: number
) => {
  return fetchAndParseSDUI(`/cart/v2/${cartId}/options`, {
    method: 'PATCH',
    body: JSON.stringify({ optionId: mealPackId })
  })
}

export const removeProductFromUnifiedCart = async (
  cartId: number,
  products: any
) => {
  return fetchSDUI(`/cart/v2/${cartId}/products`, {
    method: 'DELETE',
    body: JSON.stringify({ products })
  })
}

export const getMealPrices = async (date: string, meals: any) => {
  return fetchAndParseSDUI(`/menu/meals/price`, {
    method: 'POST',
    body: JSON.stringify({ date, meals })
  })
}

export const fetchChefs = async (date: string) => {
  if (!date) {
    return undefined
  }

  return fetchAndParseSDUI(`/chef?date=${date}`)
}

export const addProfile = async payload => {
  return fetchSDUI('/preferences/profile', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export const rateProduct = async (
  orderId: number,
  productId: number,
  payload: any
) => {
  return fetchSDUI(`/order/${orderId}/product/${productId}/review`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export const getPaymentChallenge = async payload => {
  return fetchSDUI('/payments/challenges', {
    method: 'POST',
    body: JSON.stringify(payload),
    credentials: 'include'
  })
}

export const updateUserDateOfBirth = async dateOfBirth => {
  return fetchSDUI('/user/date-of-birth', {
    method: 'PATCH',
    body: JSON.stringify({
      date_of_birth: dateOfBirth
    }),
    credentials: 'include'
  })
}

export const getViewCartForDate = async (date: string) => {
  return fetchSDUI(`/view/v1/cart/${date}`)
}

export const fetchCalendarData = async (date: string) => {
  return fetchAndParseSDUI(`/view/home/${date}/calendar`)
}

export const getViewOrdersHub = async () => {
  return fetchAndParseSDUI('/view/order-hub')
}

export const getOrderManagement = async (date: string) => {
  return fetchAndParseSDUI(`/view/order-management/${date}`)
}

export const getPastOrderRating = async (date: string) => {
  return fetchAndParseSDUI(`/view/order-hub/past-orders/${date}/rating`)
}

export const getOrderHubTimeline = async ({ date }) => {
  return fetchAndParseSDUI(`/view/order-hub/timeline/${date}`)
}

export const getOrderInvoice = async (date: string) => {
  return fetchAndParseSDUI(`/view/order-hub/past-orders/${date}`)
}

export const fetchByPath = async (path: string) => {
  return fetchAndParseSDUI(path)
}

export const searchProducts = async ({ queryText, menuDate }) => {
  const params = new URLSearchParams({ menuDate, queryText })
  return fetchAndParseSDUI(`/search?${params.toString()}`)
}

export const getMealPricesV2 = async (date, meals) => {
  const headers = await getHeaders()

  const response = await fetch(`${SDUI_SERVICE_URL}/view/v1/price-breakdown/`, {
    headers: {
      ...headers,
      'Content-type': 'application/json'
    },
    method: 'POST',
    body: JSON.stringify({ date, meals }),
    credentials: 'include'
  })

  return response.json()
}
